import { useEffect, useState } from 'react';

import { DEFAULT_SEAT_CREDIT_LIMIT } from '../../../../../../../backend/src/billing/billing-constants';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useSwitchPricingModel } from '@/api/mutations/useSwitchPricingModel';
import { Organisation } from '@/api/types/backend-dto-types';
import { dateToDatetimeLocal } from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organisation: Organisation;
}

type Step = 'form' | 'confirm';

export function SwitchPricingModelModal({ isOpen, onOpenChange, organisation }: Props) {
  const isSeatBased = organisation.pricingModel === PricingModel.SeatBased;
  const targetModel = isSeatBased ? PricingModel.Usage : PricingModel.SeatBased;

  const [step, setStep] = useState<Step>('form');
  const [seatCount, setSeatCount] = useState<string>('');
  const [fairUsageLimit, setFairUsageLimit] = useState<string>(`${DEFAULT_SEAT_CREDIT_LIMIT}`);
  const [maxCredits, setMaxCredits] = useState<string>('');
  const [billingStartDate, setBillingStartDate] = useState<string>('');
  const [billingEndDate, setBillingEndDate] = useState<string>('');

  const { mutate: switchPricingModel, isLoading } = useSwitchPricingModel(organisation.id);

  // Pre-fill billing dates from current org values
  useEffect(() => {
    if (organisation.billingStartDate) {
      setBillingStartDate(dateToDatetimeLocal(new Date(organisation.billingStartDate)));
    }
    if (organisation.billingEndDate) {
      setBillingEndDate(dateToDatetimeLocal(new Date(organisation.billingEndDate)));
    }
  }, [organisation]);

  const handleClose = (open: boolean) => {
    if (!open) {
      setStep('form');
      setSeatCount('');
      setFairUsageLimit(`${DEFAULT_SEAT_CREDIT_LIMIT}`);
      setMaxCredits('');
    }
    onOpenChange(open);
  };

  const handleNext = () => {
    setStep('confirm');
  };

  const handleConfirm = () => {
    const billingDates = {
      ...(billingStartDate && { billingStartDate: new Date(billingStartDate).toISOString() }),
      ...(billingEndDate && { billingEndDate: new Date(billingEndDate).toISOString() }),
    };

    if (targetModel === PricingModel.SeatBased) {
      switchPricingModel(
        {
          pricingModel: PricingModel.SeatBased,
          seatCount: parseInt(seatCount, 10),
          fairUsageCreditLimit: parseInt(fairUsageLimit, 10),
          ...billingDates,
        },
        { onSuccess: () => handleClose(false) },
      );
    } else {
      switchPricingModel(
        {
          pricingModel: PricingModel.Usage,
          maxCredits: parseInt(maxCredits, 10),
          ...billingDates,
        },
        { onSuccess: () => handleClose(false) },
      );
    }
  };

  const isFormValid =
    targetModel === PricingModel.SeatBased
      ? seatCount !== '' && parseInt(seatCount, 10) > 0 && fairUsageLimit !== '' && parseInt(fairUsageLimit, 10) > 0
      : maxCredits !== '' && parseInt(maxCredits, 10) >= 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            Switch to {targetModel === PricingModel.SeatBased ? 'Seat-based' : 'Usage-based'} Pricing
          </DialogTitle>
        </DialogHeader>

        {step === 'form' && (
          <>
            <div className="space-y-4 py-2">
              {targetModel === PricingModel.SeatBased ? (
                <>
                  <div className="flex items-center gap-4">
                    <Label className="basis-1/2" htmlFor="seat-count">
                      Number of Seats
                    </Label>
                    <Input
                      className="basis-1/2"
                      id="seat-count"
                      type="number"
                      value={seatCount}
                      onChange={(e) => setSeatCount(e.target.value)}
                      placeholder="e.g. 10"
                      min="1"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <Label className="basis-1/2" htmlFor="fair-usage-limit">
                      Fair Usage Credit Limit
                    </Label>
                    <Input
                      className="basis-1/2"
                      id="fair-usage-limit"
                      type="number"
                      value={fairUsageLimit}
                      onChange={(e) => setFairUsageLimit(e.target.value)}
                      placeholder={`e.g. ${DEFAULT_SEAT_CREDIT_LIMIT}`}
                      min="1"
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-4">
                  <Label className="basis-1/2" htmlFor="max-credits">
                    Maximum Credits
                  </Label>
                  <Input
                    className="basis-1/2"
                    id="max-credits"
                    type="number"
                    value={maxCredits}
                    onChange={(e) => setMaxCredits(e.target.value)}
                    placeholder="e.g. 100000"
                    min="0"
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <Label className="basis-1/2" htmlFor="billing-start">
                  Billing Period Start Date
                </Label>
                <Input
                  className="basis-1/2"
                  id="billing-start"
                  type="datetime-local"
                  value={billingStartDate}
                  onChange={(e) => setBillingStartDate(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-4">
                <Label className="basis-1/2" htmlFor="billing-end">
                  Billing Period End Date
                </Label>
                <Input
                  className="basis-1/2"
                  id="billing-end"
                  type="datetime-local"
                  value={billingEndDate}
                  onChange={(e) => setBillingEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => handleClose(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleNext} disabled={!isFormValid}>
                Next
              </Button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <Icon className="lucide--triangle-alert mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="space-y-2 text-sm text-amber-800">
                <p className="font-medium">This is a significant change.</p>
                {targetModel === PricingModel.SeatBased ? (
                  <ul className="list-inside list-disc space-y-1">
                    <li>All users on the Member role will be reassigned to the Seat-based role</li>
                    <li>Credit limits will be created for all existing users</li>
                    <li>Blocking will be enabled and all runs will require self-approval</li>
                    <li>The org-level credit budget will be cleared</li>
                  </ul>
                ) : (
                  <ul className="list-inside list-disc space-y-1">
                    <li>All users on the Seat-based role will be reassigned to the Member role</li>
                    <li>All per-user credit limits will be closed</li>
                    <li>Blocking will be disabled and any blocked users will be unblocked</li>
                    <li>The token approval limit will be restored to the default (100,000)</li>
                  </ul>
                )}
                <p className="font-medium">Are you sure you want to proceed?</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={() => setStep('form')} disabled={isLoading}>
                Back
              </Button>
              <Button variant="destructive" onClick={handleConfirm} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
                    Switching...
                  </>
                ) : (
                  'Confirm Switch'
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
